import google from '@/assets/svgs/icons/google.svg';
import microsoft from '@/assets/svgs/icons/microsoft.svg';
import CarouselBox from '@/components/Login/CarouselBox.tsx';
import { useWhitelabel } from '@/contexts/WhitelabelContext';
import * as Sentry from '@sentry/react';
import { useDebounce } from '@superfunnel/react-hooks/useDebounce';
import { SSOProvider } from '@superfunnel/types';
import Button from '@superfunnel/ui-core/v2/Button';
import Input from '@superfunnel/ui-core/v2/Input';
import Label from '@superfunnel/ui-core/v2/Label';
import { emailIsValid } from '@superfunnel/utils';
import {
  getAuth,
  getRedirectResult,
  isSignInWithEmailLink,
} from 'firebase/auth';
import { Mail } from 'iconoir-react';
import { useCallback, useContext, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';
import { LAST_USED_ACCOUNT_STORAGE_KEY } from '../constants/storage';
import { useAnalytics } from '../contexts/AnalyticsContext';
import { AuthContext } from '../contexts/AuthContext';
import { auth } from '../Firebase';

const Login = () => {
  const { track } = useAnalytics();
  const {
    isAuthenticated,
    loginWithGoogle,
    loginWithMicrosoft,
    loginWithEmailLink,
    sendLoginLinkToEmail,
    loginWithSAML,
    getSSOAuthProvider,
  } = useContext(AuthContext);

  const location = useLocation();
  const params = useParams();

  const [, setError] = useState<string>();
  const [loading, setLoading] = useState<
    'google' | 'microsoft' | 'email' | 'sso' | undefined
  >(undefined);
  const [loginType, setLoginType] = useState<'email' | 'sso'>('email');
  useState<boolean>(false);
  const [showSSO, setShowSSO] = useState<{
    validEmail: boolean;
    authProvider?: SSOProvider;
  }>();
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState<string | undefined>();

  const debouncedEmail = useDebounce(email, 500);

  const navigate = useNavigate();

  const postLogin = useCallback(async () => {
    const lastUsedWorkspace = window.localStorage.getItem(
      LAST_USED_ACCOUNT_STORAGE_KEY,
    );
    if (lastUsedWorkspace) {
      navigate(`/workspace/${lastUsedWorkspace}`);
    } else {
      navigate('/');
    }
  }, [navigate]);

  useEffect(() => {
    // Confirm the link is a sign-in with email link.
    if (isAuthenticated) {
      postLogin();
    } else {
      const auth = getAuth();
      if (isSignInWithEmailLink(auth, window.location.href)) {
        let email = window.localStorage.getItem('emailForSignIn');
        if (!email) {
          email = window.prompt('Please provide your email for confirmation');
        } else {
          loginWithEmailLink(auth, email, window.location.href);
        }
      }
    }
  }, [
    location.pathname,
    params,
    isAuthenticated,
    postLogin,
    loginWithEmailLink,
  ]);

  useEffect(() => {
    if (!debouncedEmail) {
      return;
    }
    const isValid = emailIsValid(debouncedEmail);
    setShowSSO({ validEmail: isValid });

    if (isValid) {
      let relevant = true;
      getSSOAuthProvider(debouncedEmail).then((authProvider) => {
        if (!relevant) return;

        setShowSSO({ validEmail: isValid, authProvider });
      });

      return () => {
        relevant = false;
      };
    }
  }, [debouncedEmail, getSSOAuthProvider]);

  const { appName, logoFull } = useWhitelabel();

  const onLogin = async (
    provider: 'google' | 'microsoft' | 'email' | SSOProvider,
  ) => {
    const providerName = typeof provider === 'string' ? provider : provider.id;

    track('login_attempt', {
      provider: providerName,
      login_type: loginType,
    });

    const newLoading =
      provider === 'google' || provider === 'microsoft' || provider === 'email'
        ? provider
        : 'sso';

    setError(undefined);
    setLoading(newLoading);
    if (provider === 'google') {
      loginWithGoogle()
        .then(() => {
          track('login_success', { provider: 'google' });
          postLogin();
        })
        .catch((error) => {
          track('login_failed', {
            provider: 'google',
            error: error.message,
          });
          throw error;
        })
        .finally(() => setLoading(undefined));
    } else if (provider === 'microsoft') {
      loginWithMicrosoft()
        .then(() => {
          track('login_success', { provider: 'microsoft' });
          postLogin();
        })
        .catch((error) => {
          track('login_failed', {
            provider: 'microsoft',
            error: error.message,
          });
          throw error;
        })
        .finally(() => setLoading(undefined));
    } else if (provider === 'email') {
      if (!emailIsValid(email)) {
        track('login_failed', {
          provider: 'email',
          error: 'Invalid email format',
        });
        return;
      }
      sendLoginLinkToEmail(email)
        .then(() => {
          window.localStorage.setItem('emailForSignIn', email);
          setMessage('Login link sent to your email');
          track('login_link_sent', {
            provider: 'email',
            email_domain: email.split('@')[1],
          });
        })
        .catch((error) => {
          track('login_link_failed', {
            provider: 'email',
            error: error.message,
          });
          throw error;
        })
        .finally(() => setLoading(undefined));
    } else if ('id' in provider) {
      loginWithSAML(provider.id)
        .then(() => {
          track('login_success', {
            provider: 'sso',
            sso_provider_id: provider.id,
          });
          postLogin();
        })
        .catch((error) => {
          track('login_failed', {
            provider: 'sso',
            sso_provider_id: provider.id,
            error: error.message,
          });
          throw error;
        })
        .finally(() => setLoading(undefined));
    } else {
      console.error('Unknown provider', provider);
      track('login_failed', {
        provider: 'unknown',
        error: 'Unknown provider',
      });
    }
  };

  useEffect(() => {
    getRedirectResult(auth)
      .then((result) => {
        if (result) {
          track('login_redirect_success', {
            provider: result.user.providerData[0]?.providerId || 'unknown',
          });
        }
      })
      .catch((e) => {
        setError(e.message);
        track('login_redirect_failed', {
          error: e.message,
        });
        Sentry.captureException(e);
      })
      .finally(() => {
        setLoading(undefined);
      });
  }, [track]);

  useEffect(() => {
    track('login_screen_viewed');
  }, [track]);

  return (
    <div className="grid min-h-full grid-cols-1 bg-white lg:grid-cols-2">
      <div
        className={'flex w-full items-center justify-center lg:py-2 lg:pl-2'}
      >
        <div className="max-w-100 mx-auto flex w-full flex-col items-center justify-center gap-8 px-4 py-6 lg:px-0 lg:py-0">
          <img className="h-7" src={logoFull} alt={appName} />
          <div className={'flex flex-col items-center gap-3 text-center'}>
            <h2 className="text-base-900 text-[32px]/[1.2] font-medium -tracking-[0.01em]">
              Welcome to {appName}
            </h2>
            <p className={'text-base-700 text-[14px]/[1.4] tracking-[0.01em]'}>
              From first click to final conversion, our AI builds and
              <br />
              improves your funnels on autopilot.
            </p>
          </div>
          <div className={'grid w-full gap-4'}>
            <div className="grid gap-3 tracking-[0.03em]">
              <Button
                variant={'outline'}
                onClick={() => onLogin('google')}
                loading={loading === 'google'}
                disabled={!!loading}
                className={
                  'text-base-900 rounded-2lg w-full py-[9px] text-[15px]/[1]'
                }
              >
                <img src={google} alt={'Google'} />
                <span className="text-sm font-semibold leading-6">
                  Sign in with Google
                </span>
              </Button>
              <Button
                variant={'outline'}
                onClick={() => onLogin('microsoft')}
                loading={loading === 'microsoft'}
                disabled={!!loading}
                className={
                  'text-base-900 rounded-2lg w-full py-[9px] text-[15px]/[1]'
                }
              >
                <img src={microsoft} alt={'Microsoft'} />
                <span className="text-sm font-semibold leading-6">
                  Sign in with Microsoft
                </span>
              </Button>
              {loginType === 'email' ? (
                <Button
                  variant={'outline'}
                  loading={loading === 'sso'}
                  disabled={!!loading}
                  onClick={() => {
                    track('login_method_switched', {
                      from: 'email',
                      to: 'sso',
                    });
                    setLoginType('sso');
                    setShowSSO({ validEmail: false });
                  }}
                  className={
                    'text-base-900 rounded-2lg w-full py-[9px] text-[15px]/[1]'
                  }
                >
                  <Mail className="size-6" />
                  <span className="text-sm font-semibold leading-6">
                    Sign in With SSO
                  </span>
                </Button>
              ) : (
                <Button
                  variant={'outline'}
                  loading={loading === 'email'}
                  disabled={!!loading}
                  onClick={() => {
                    track('login_method_switched', {
                      from: 'sso',
                      to: 'email',
                    });
                    setLoginType('email');
                  }}
                  className={
                    'text-base-900 rounded-2lg w-full py-[9px] text-[15px]/[1]'
                  }
                >
                  <Mail className="size-6" />
                  <span className="text-sm font-semibold leading-6">
                    Sign in with email
                  </span>
                </Button>
              )}
            </div>

            <div
              className={
                'before:bg-base-200 relative flex items-center justify-center before:absolute before:left-0 before:top-1/2 before:h-px before:w-full before:-translate-y-1/2 before:transform'
              }
            >
              <p
                className={
                  'text-base-500 relative z-10 block bg-white px-4 text-[14px]/[1.4] -tracking-[0.01em]'
                }
              >
                or
              </p>
            </div>

            <div className="flex w-full flex-col gap-4">
              {loginType === 'email' && (
                <div className="grid w-full items-center gap-1">
                  <Label
                    htmlFor="email"
                    className={
                      'text-base-700 font-medium leading-[1.4] tracking-[0.01em]'
                    }
                  >
                    Sign in with email
                  </Label>
                  <Input
                    key={'emailinkfield'}
                    type="email"
                    id="email"
                    placeholder="name@company.com"
                    autoComplete="email"
                    value={email}
                    readOnly={!!loading}
                    className={
                      'w-full px-3 [&>input]:py-2 [&>input]:text-base/normal [&>input]:tracking-[0.01em]'
                    }
                    onChange={(e) => setEmail(e.target.value)}
                    validation={
                      email === ''
                        ? {}
                        : {
                            status: !emailIsValid(email) ? 'error' : 'success',
                            message: !emailIsValid(email)
                              ? 'Invalid email format'
                              : '',
                          }
                    }
                  />
                </div>
              )}

              {loginType === 'sso' && !!showSSO && (
                <div className="grid w-full items-center gap-1">
                  <Label
                    htmlFor="email"
                    className={
                      'text-base-700 font-medium leading-[1.4] tracking-[0.01em]'
                    }
                  >
                    Sign in With SSO
                  </Label>
                  <Input
                    key={'ssofield'}
                    type="email"
                    autoComplete="email"
                    id="email"
                    placeholder="name@company.com"
                    value={email}
                    readOnly={!!loading}
                    className={
                      'w-full px-3 [&>input]:py-2 [&>input]:text-base/normal [&>input]:tracking-[0.01em]'
                    }
                    onChange={(e) => setEmail(e.target.value)}
                    validation={
                      email === ''
                        ? {}
                        : {
                            status: !emailIsValid(email) ? 'error' : 'success',
                            message: !emailIsValid(email)
                              ? 'Invalid email format'
                              : '',
                          }
                    }
                  />
                </div>
              )}

              <div className={'space-y-2'}>
                {loginType === 'email' && (
                  <Button
                    disabled={!emailIsValid(email) || !!loading}
                    onClick={() => {
                      onLogin('email');
                    }}
                    loading={loading === 'email'}
                    className={'w-full'}
                  >
                    Get login link
                  </Button>
                )}

                {loginType === 'sso' && !!showSSO && (
                  <Button
                    disabled={
                      !emailIsValid(email) || !showSSO.authProvider || !!loading
                    }
                    onClick={() => {
                      if (!showSSO.authProvider) {
                        return;
                      }
                      onLogin(showSSO.authProvider);
                    }}
                    className={'w-full'}
                    loading={loading === 'sso'}
                  >
                    Sign In
                  </Button>
                )}

                {message && <p className="text-sm text-green-600">{message}</p>}
                <p className="text-base-500 text-center text-[10px]/[1.4] tracking-[0.01em]">
                  By continuing, you accept our{' '}
                  <a
                    href="https://superfunnel.ai/terms"
                    target="_blank"
                    rel="noreferrer"
                    className={'underline'}
                  >
                    Terms of Service
                  </a>{' '}
                  and{' '}
                  <a
                    href="https://superfunnel.ai/privacy"
                    target="_blank"
                    rel="noreferrer"
                    className={'underline'}
                  >
                    Privacy Policy
                  </a>
                  .
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      <CarouselBox />
    </div>
  );
};

export default Login;
